'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';

interface Version {
  id: string;
  version_number: string;
  changelog: string;
  created_at: string;
}

export default function TemplateVersionChangelog({ templateId }: { templateId: string }) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('template_versions')
        .select('*')
        .eq('template_id', templateId)
        .order('created_at', { ascending: false })
        .limit(5);
      setVersions(data || []);
      setLoading(false);
    };
    load();
  }, [templateId]);

  if (loading || versions.length === 0) return null;

  const latest = versions[0];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <div
        className="px-5 py-4 flex items-center justify-between cursor-pointer"
        style={{ borderBottom: expanded ? '1px solid var(--border)' : 'none' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon name="SparklesIcon" size={16} style={{ color: 'var(--accent)' }} />
          <div>
            <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>What's New</h3>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Version {latest.version_number} · {new Date(latest.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <Icon name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} style={{ color: 'var(--text-secondary)' }} />
      </div>

      {expanded && (
        <div className="p-5 space-y-4">
          {versions.map((v, i) => (
            <div key={v.id} className={i > 0 ? 'pt-4' : ''} style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: i === 0 ? 'var(--accent-light)' : 'var(--bg-secondary)', color: i === 0 ? 'var(--accent)' : 'var(--text-secondary)' }}>
                  v{v.version_number}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{new Date(v.created_at).toLocaleDateString()}</span>
                {i === 0 && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#10b98122', color: '#10b981' }}>Latest</span>}
              </div>
              <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>{v.changelog}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
